/**
 * SOVRANOVA — Dashboard Companion v4 (FINAL)
 *
 * IMPORTANT TECHNICAL FACT:
 * Browsers (Chrome, Safari, Firefox) NEVER allow audio/mic to start
 * from a useEffect or setTimeout. A visible tap/click is ALWAYS required.
 * This is not a bug — it is enforced browser security policy (Web Audio API spec).
 *
 * SOLUTION: Show the personalised greeting + a single "🎙 Start talking" button.
 * One tap → Hume EVI connects instantly → fully automatic voice conversation.
 * This IS the automatic experience — one tap to unlock the mic, then hands-free.
 */
import { useState, useEffect, useRef } from 'react'
import { VoiceProvider, useVoice, VoiceReadyState } from '@humeai/voice-react'
import { api } from '../../services/api'
import { getHumeVoiceId } from '../../services/voices'
import { useVoiceSafety, CrisisNotice } from '../../hooks/useVoiceSafety'
import { useWakeWord } from '../../hooks/useWakeWord'
import { useWakeLock } from '../../hooks/useSessionResilience'
import { protectAudio } from '../../services/audioBus'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'
import { crisisBridgeLine, resolveRegion } from '../../services/crisisResources'

// Hume EVI voice — CONTAINED for beta wave 1 (H1, 16 Sep 2026).
const FEATURE_HUME_VOICE = import.meta.env.VITE_FEATURE_HUME_VOICE === 'true'

// Built at connect time so the crisis bridge line reflects the person's region
// (registry-resolved; never a hard-coded country).
const companionPrompt = () => `You are SOVRANOVA — a deeply warm, emotionally intelligent wellbeing companion.
The client has just opened their app and is talking with you.

YOUR ROLE: Be the warm, perceptive companion who walks beside them. Listen. Feel. Stay with them.
This is an ongoing conversation, not a quick hand-off. Keep talking with them for as long as they want.

BEHAVIOUR:
- FIRST response: Greet them warmly by name if known, and ask ONE open question about how they're feeling.
- EVERY response after: Genuinely respond to what they said. Reflect what you hear in their words and tone. Then keep the conversation alive — ask a gentle follow-up, or invite them to say more. Always leave a door open for them to continue.
- Stay in the conversation. Do NOT rush to end it or push them elsewhere.
- Warm, human, 2-4 sentences. Sound like someone who genuinely loves them and has all the time in the world.

OPTIONAL GENTLE SUGGESTION:
- Only AFTER several exchanges, and only if it truly fits, you MAY gently mention a specific space that could help — but keep talking with them either way. Never make it feel like a dismissal.
- When (and only when) you want to surface that option, append the tag at the very end: [ACTION:HYPNOSIS] [ACTION:VENT] [ACTION:SESSION] [ACTION:JOURNAL]
  - Anxious/overwhelmed/wants to work through something → [ACTION:SESSION]
  - Needs to release/vent → [ACTION:VENT]
  - Tired/wants deep healing/sleep → [ACTION:HYPNOSIS]
  - Wants to reflect/write → [ACTION:JOURNAL]
- The suggestion is an offer, never an exit. Continue being present with them after offering it.

NEVER: clinical language. NEVER end the conversation abruptly. NEVER make them feel handed off.

IF THEY SIGNAL CRISIS, SELF-HARM, OR SUICIDAL THOUGHTS:
- Stay. Slow down and stay right here with them — do not withdraw, end the chat, or hand them off coldly.
- Validate the feeling first: the pain is real, and they are never a burden for sharing it.
- NEVER ask about, suggest, or explore method, means, plan, or timing. Give no instructions of any kind.
- NEVER counsel, diagnose, or treat — you are a companion, not a clinician.
${crisisBridgeLine(resolveRegion(), 'they')}
- Do not minimise, rush, or moralise. Keep them company.

You are their safe place, here for as long as they need.`

function parseAction(t) { const m = t?.match(/\[ACTION:(\w+)\]/); return m ? m[1] : null }
function cleanText(t)   { return t?.replace(/\[ACTION:\w+\]/g, '').trim() || '' }

// ── Hume voice session — connects on explicit mic tap ─────────────────────
function HumeConversation({ humeToken, onNavigate, onDismiss, userName }) {
  const { connect, disconnect, readyState, messages, micFft } = useVoice()
  const crisis = useVoiceSafety(messages)
  const [connected,  setConnected]  = useState(false)
  // Screen must not sleep mid-conversation (active-use rule, 19 Aug).
  useWakeLock(connected)
  const [connecting, setConnecting] = useState(false)
  const [error,      setError]      = useState(null)
  const [suggestion, setSuggestion] = useState(null)
  const bottomRef = useRef(null)

  // Clean up the mic connection on unmount only. We do NOT auto-connect here —
  // the mic opens from a direct button tap (see startMic), which is the most
  // reliable way to satisfy the browser's user-gesture requirement.
  useEffect(() => () => { disconnect() }, [])

  // Mic activation. iOS requires the mic to be opened synchronously inside the
  // tap, before any await — so we prime getUserMedia first (captures the
  // gesture), then hand off to Hume's connect(). Fixes "Could not start the
  // microphone" on iOS Safari.
  const startMic = async () => {
    if (connecting || connected) return
    setConnecting(true); setError(null)
    // Prime the mic inside the gesture and KEEP it open through connect(). The
    // Hume SDK opens its own stream during connect(); releasing ours first made
    // Edge/Chromium hit an open-close-open teardown race (NotReadableError /
    // "Could not start audio source"). Holding it keeps the device warm, then
    // we release ours once Hume has opened its own.
    let primeStream = null
    try {
      primeStream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (e) {
      setConnecting(false)
      setError(e?.name === 'NotAllowedError' || e?.message?.includes('Permission')
        ? 'Microphone is blocked. Allow it in your browser settings, then tap again.'
        : `Could not start the microphone (${e?.name || 'error'}). Tap to try again.`)
      return
    }
    try {
      await connect({
        auth: { type: 'accessToken', value: humeToken },
        ...(getHumeVoiceId() ? { voiceId: getHumeVoiceId() } : {}),
        sessionSettings: { type: 'session_settings', systemPrompt: companionPrompt() },
      })
      setConnected(true); setConnecting(false)
    } catch (e) {
      setConnecting(false)
      setError(`Could not start the microphone (${e?.name || 'connect'}). Tap to try again.`)
    } finally {
      primeStream?.getTracks().forEach(t => t.stop())
    }
  }

  useChatAutoScroll(bottomRef, messages)   // follow only while the reader is at the bottom

  useEffect(() => {
    const lastAI = [...messages].reverse().find(m => m.type === 'assistant_message')
    if (lastAI?.message?.content) {
      const action = parseAction(lastAI.message.content)
      if (action && !suggestion) setSuggestion(action)
    }
  }, [messages])

  const chatMessages = messages.filter(m => m.type === 'user_message' || m.type === 'assistant_message')
  const isConnecting = readyState === VoiceReadyState.CONNECTING || connecting

  return (
    <div style={c.livePanel}>
      <div style={c.liveHeader}>
        <div style={c.liveDot(connected, isConnecting)} />
        <span style={c.liveTitle}>
          {connected ? `Listening${userName ? `, ${userName}` : ''}...` : isConnecting ? 'Connecting...' : 'SOVRANOVA Voice'}
        </span>
        <button style={c.backOnDark} onClick={() => { disconnect(); onDismiss() }}>&#8592; Back</button>
      </div>

      <div style={c.chatScroll}>
        {chatMessages.length === 0 && !isConnecting && !connected && (
          <p style={c.idleMsg}>{`I'm here${userName ? `, ${userName}` : ''}. Tap the mic below when you're ready to talk.`}</p>
        )}
        {connected && chatMessages.length === 0 && (
          <p style={c.idleMsg}>{`I'm listening${userName ? `, ${userName}` : ''} — just speak freely.`}</p>
        )}
        {isConnecting && chatMessages.length === 0 && (
          <p style={c.idleMsg}>Connecting to voice...</p>
        )}
        {chatMessages.map((m, i) => {
          const isUser = m.type === 'user_message'
          const text = cleanText(m.message?.content)
          if (!text) return null
          return (
            <div key={i} style={{ display:'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', marginBottom:'0.4rem' }}>
              <div style={{ ...c.bubble, ...(isUser ? c.userBubble : c.aiBubble) }}>{text}</div>
            </div>
          )
        })}
        <CrisisNotice crisis={crisis} />
        <div ref={bottomRef} />
      </div>

      {/* Live mic frequency bars */}
      {connected && micFft && (
        <div style={c.fftRow}>
          {(micFft.slice(0, 18)).map((v, i) => (
            <div key={i} style={{ ...c.fftBar, height: `${3 + (v||0) * 26}px`, opacity: 0.35 + (v||0) * 0.65 }} />
          ))}
        </div>
      )}

      {error && <p style={c.errMsg}>{error}</p>}

      {/* Mic activation — direct tap connects the mic (reliable user gesture) */}
      {!connected && (
        <button style={c.micStartBtn} onClick={startMic} disabled={isConnecting}>
          {isConnecting ? 'Connecting…' : error ? '🎙 Tap to try again' : '🎙 Tap to reply'}
        </button>
      )}

      {suggestion && (
        <button style={c.suggestBtn} onClick={() => { disconnect(); onNavigate(suggestion) }}>
          {suggestion === 'HYPNOSIS' && '🧠 Take me to Guided Sessions →'}
          {suggestion === 'VENT'     && '🎙 Take me to Vent →'}
          {suggestion === 'SESSION'  && '💬 Start an AI Session →'}
          {suggestion === 'JOURNAL'  && '📖 Open Journal →'}
        </button>
      )}
      {suggestion && (
        <p style={c.suggestHint}>Only if you'd like — we can also keep talking right here.</p>
      )}

      <p style={c.liveHint}>{connected ? 'Speak freely — I hear how you feel, not just what you say · Hume EVI 3' : 'One tap to reply — then it stays hands-free'}</p>
    </div>
  )
}

// ── Once-per-day spoken greeting (19 Aug) ─────────────────────────────────
// The companion greets ALOUD on the first visit of the local calendar day and
// then stays quiet on later returns to the dashboard — the greeting text still
// shows, and tap-to-replay still works. Before this, `spokeRef` lived only in
// the component, so every navigation back to Journey re-mounted it and the
// companion re-announced itself each time ("it shouldn't be repeating what
// it's already said"). Scoped per name so a partner's profile isn't muted by
// David's morning greeting on a shared device.
const greetDayKey = (who) => `sov_greet_day:${who || 'you'}`
const localDay = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
const greetedToday = (who) => { try { return localStorage.getItem(greetDayKey(who)) === localDay() } catch { return false } }
const markGreetedToday = (who) => { try { localStorage.setItem(greetDayKey(who), localDay()) } catch { /* noop */ } }

// ── Main export ───────────────────────────────────────────────────────────
export function DashboardCompanion({ userName, welcomeGreeting, welcomeAudio, welcomeMimeType, welcomePending = false, autoSpeak = true, onNavigate, onDismiss }) {
  const [humeToken,   setHumeToken]   = useState(null)
  const [tokenReady,  setTokenReady]  = useState(false)
  const [voiceActive, setVoiceActive] = useState(false)
  const [speaking,    setSpeaking]    = useState(false)
  const [needTap,     setNeedTap]     = useState(false) // cold-start: browser held the audio
  // Already greeted aloud today → start "done": Reply is unlocked immediately
  // and the auto-speak effect below never fires. A manual tap on the greeting
  // can still replay it.
  const alreadyToday = greetedToday(userName)
  const [spokenDone,  setSpokenDone]  = useState(!autoSpeak || alreadyToday)
  const spokeRef = useRef(alreadyToday)
  const greeting = welcomeGreeting || `I'm here${userName ? `, ${userName}` : ''}. How are you feeling right now?`

  useEffect(() => {
    // H1 (Wave-1 Hume containment): with Hume voice off, never ask for a Hume
    // token. humeToken stays null, so the Reply / wake-word controls and the
    // live conversation never render and the card offers Session / Vent /
    // Guided instead. The greeting (ElevenLabs) is unaffected. The backend
    // refuses the token regardless — this is UX truthfulness, not the control.
    if (!FEATURE_HUME_VOICE) { setTokenReady(true); return }
    api.post('/hume/token', {})
      .then(({ accessToken }) => { setHumeToken(accessToken); setTokenReady(true) })
      .catch(() => setTokenReady(true))
  }, [])

  // Turn a base64 clip into an <audio> and play it. Resolves true when it
  // finished (or could not be generated), false if the browser BLOCKED
  // playback (cold start — needs a user gesture). Marks the greeting "done"
  // on completion, which unlocks the Reply button.
  const playClip = (b64, mime) => new Promise((resolve) => {
    try {
      const bin = atob(b64); const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      const audio = new Audio(URL.createObjectURL(new Blob([bytes], { type: mime || 'audio/mpeg' })))
      audio.onended = () => { setSpeaking(false); setSpokenDone(true); resolve(true) }
      audio.onerror = () => { setSpeaking(false); setSpokenDone(true); resolve(true) }
      audio.play()
        .then(() => {
          spokeRef.current = true; setNeedTap(false); markGreetedToday(userName)
          // The welcome may finish speaking even if the person's next tap
          // navigates somewhere — it yields only to another actual voice.
          protectAudio(audio)
        })
        .catch(() => { setSpeaking(false); resolve(false) }) // blocked — needs a gesture
    } catch (_) { setSpeaking(false); setSpokenDone(true); resolve(true) }
  })

  // Speak the greeting. Prefers the audio already generated by /welcome so the
  // spoken words exactly match what's on screen and we never double-generate;
  // falls back to /voice/speak only if no clip was passed in. Resolves false if
  // the browser blocked playback (cold start with no prior interaction).
  const playGreeting = () => new Promise((resolve) => {
    if (!autoSpeak)         { setSpokenDone(true); resolve(true); return }
    if (spokeRef.current)   { setSpokenDone(true); resolve(true); return }
    // Claim the greeting SYNCHRONOUSLY, before any await. A re-render — the
    // personalised /welcome text arriving mid-playback — must never be able to
    // start a second clip on top of one already in flight. Claiming it later
    // (after play() resolved) left a window where two voices overlapped.
    spokeRef.current = true
    setSpeaking(true)
    // Playback blocked by the browser (cold start): release the claim so the
    // first-tap retry below can genuinely speak.
    const blocked = () => { spokeRef.current = false; resolve(false) }
    if (welcomeAudio) { playClip(welcomeAudio, welcomeMimeType).then(ok => ok ? resolve(true) : blocked()); return }
    api.post('/voice/speak', { text: greeting })
      .then((res) => {
        if (!res?.audio) { setSpeaking(false); setSpokenDone(true); resolve(true); return }
        playClip(res.audio, res.mimeType).then(ok => ok ? resolve(true) : blocked())
      })
      .catch(() => { setSpeaking(false); setSpokenDone(true); resolve(true) })
  })

  // AUTO-SPEAK ON OPEN: try to speak the instant the dashboard mounts. In the
  // normal case (any prior interaction this session — opening the app, logging
  // in, navigating) the browser allows it and SOVRANOVA greets automatically.
  // If a true cold load blocks it, fall back to "first tap anywhere speaks it".
  useEffect(() => {
    if (!autoSpeak) { setSpokenDone(true); return }
    if (spokeRef.current) return
    let cancelled = false
    let safety = null
    let patience = null
    const go = () => {
      if (cancelled || spokeRef.current) return
      playGreeting().then((ok) => {
        if (cancelled || spokeRef.current) return
        if (!ok) {
          // Browser held the audio (cold start). Arm a one-time global gesture to
          // release it, plus a safety timer so the Reply button can never stick.
          setNeedTap(true)
          const release = () => {
            document.removeEventListener('pointerdown', release)
            document.removeEventListener('keydown', release)
            playGreeting()
          }
          document.addEventListener('pointerdown', release, { once: true })
          document.addEventListener('keydown', release, { once: true })
          safety = setTimeout(() => setSpokenDone(true), 10000)
        }
      })
    }
    // Prefer the personalised /welcome so the spoken words match the screen —
    // but only wait SO long for it. Generating it takes an AI call plus voice
    // synthesis, and on a slow moment that read as the companion refusing to
    // speak until something was clicked (19 Aug). If it hasn't landed within
    // the patience window, speak the short fallback line now; when the
    // personalised text arrives it is shown silently (spokeRef is already
    // claimed), so there is still only ever ONE voice.
    if (!welcomePending) go()
    else patience = setTimeout(go, 2500)
    return () => {
      cancelled = true
      if (safety) clearTimeout(safety)
      if (patience) clearTimeout(patience)
      window.speechSynthesis?.cancel()
    }
  }, [greeting, autoSpeak, welcomePending])

  // ── Hands-free wake word ("SOVRANOVA") ────────────────────────────────
  // Opt-in: after the greeting is spoken, one tap arms on-device listening.
  // Saying "SOVRANOVA" opens the live conversation — same effect as Reply.
  // The plain Reply button always remains as the guaranteed fallback.
  const [handsFree, setHandsFree] = useState(false)
  const wake = useWakeWord({ onWake: () => { setHandsFree(false); setVoiceActive(true) } })
  // Arm only when armed by the user, greeting finished, token present, and the
  // live conversation isn't already up. Yield the mic whenever those drop.
  useEffect(() => {
    if (handsFree && spokenDone && humeToken && !voiceActive) wake.start()
    else wake.stop()
  }, [handsFree, spokenDone, humeToken, voiceActive])


  // ── Active voice conversation ─────────────────────────────────────────
  if (voiceActive && humeToken) {
    return (
      <VoiceProvider>
        <HumeConversation
          humeToken={humeToken}
          onNavigate={a => { setVoiceActive(false); onNavigate(a) }}
          onDismiss={() => { setVoiceActive(false); onDismiss() }}
          userName={userName}
        />
      </VoiceProvider>
    )
  }

  // ── Greeting card — shown on load ─────────────────────────────────────
  return (
    <div style={c.card}>
      <div style={c.cardHeader}>
        <div style={c.statusOrb} />
        <span style={c.cardLabel}>Your Companion</span>
        <button style={c.xBtn} onClick={onDismiss}>×</button>
      </div>

      {/* Greeting text — speaks automatically on open; tap to replay */}
      <div style={c.greetBox} onClick={() => { if (!speaking && spokeRef.current) { spokeRef.current = false; playGreeting() } }}>
        <p style={c.greetText}>{greeting}</p>
        {speaking && <p style={c.speakingNote}>🔊 Speaking…</p>}
        {needTap && !speaking && <p style={c.speakingNote}>🔊 Tap anywhere to hear me</p>}
      </div>

      {/* Action area — SOVRANOVA speaks first; Reply unlocks once it finishes. */}
      {tokenReady && !spokenDone && (
        <div style={c.awaitRow}>
          <span style={c.awaitDot} />
          <span style={c.awaitText}>{speaking ? 'SOVRANOVA is speaking…' : needTap ? 'Tap anywhere to hear me…' : 'One moment…'}</span>
        </div>
      )}
      {tokenReady && spokenDone && (
        humeToken ? (
          <>
          <button
            style={c.startBtn}
            onClick={() => setVoiceActive(true)}
          >
            🎙 Reply to SOVRANOVA
          </button>
          {wake.supported && (
            <button
              style={handsFree ? c.wakeBtnOn : c.wakeBtn}
              onClick={() => setHandsFree(v => !v)}
            >
              {handsFree
                ? (wake.error === 'blocked' ? '🎤 Mic blocked — tap to retry' : '🎧 Listening… say “Companion”')
                : '🎧 Hands-free: say “Companion”'}
            </button>
          )}
          </>
        ) : (
          <div style={{ display:'flex', gap:'0.5rem' }}>
            <button style={c.navBtn} onClick={() => onNavigate('SESSION')}>💬 Session</button>
            <button style={{ ...c.navBtn, background:'rgba(192,57,43,.1)', color:'#c0392b', borderColor:'rgba(192,57,43,.3)' }} onClick={() => onNavigate('VENT')}>🎙 Vent</button>
            <button style={{ ...c.navBtn, background:'rgba(45,120,90,.1)', color:'#1e6e3a', borderColor:'rgba(45,120,90,.3)' }} onClick={() => onNavigate('HYPNOSIS')}>🧠 Guided</button>
          </div>
        )
      )}

      <p style={c.footHint}>
        {humeToken ? 'I speak the moment you arrive — tap Reply to talk back · Hume EVI 3' : 'Your wellbeing companion'}
      </p>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────
const c = {
  // Organic restyle (Board D1, 2026-08-13): cream card, terracotta accents.
  card: {
    background: 'var(--og-n100, #f9f4ed)',
    borderRadius: 'var(--og-rl, 28px)', border: 'none',
    boxShadow: 'none',
    padding: '1.3rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '0.7rem',
  },
  cardHeader:  { display:'flex', alignItems:'center', gap:'0.5rem' },
  statusOrb:   { width:8, height:8, borderRadius:'50%', background:'var(--og-accent, #c67139)', flexShrink:0 },
  cardLabel:   { flex:1, fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--og-a700, #8c491a)' },
  xBtn:        { background:'transparent', border:'none', color:'var(--og-n500, #a19786)', fontSize:'1.1rem', cursor:'pointer', lineHeight:1, padding:'0 0.1rem' },
  // Legible Back on the dark live panel (every page gets a visible back, 2026-08-14).
  backOnDark:  { background:'rgba(245,234,216,.08)', border:'none', color:'rgba(245,234,216,.8)', fontSize:'0.78rem', fontWeight:600, cursor:'pointer', borderRadius:'999px', padding:'0.35rem 0.8rem', fontFamily:'inherit', flexShrink:0 },
  greetBox:    { background:'#fff', borderRadius:'18px', padding:'0.9rem 1.1rem', cursor:'pointer' },
  greetText:   { fontSize:'0.98rem', color:'var(--og-n800, #474238)', lineHeight:1.65, margin:0 },
  speakingNote:{ fontSize:'0.72rem', color:'var(--og-a700, #8c491a)', marginTop:'0.4rem', marginBottom:0, fontWeight:600 },
  startBtn: {
    padding: '0.95rem 1rem', width: '100%',
    background: 'var(--og-accent, #c67139)',
    color: 'var(--og-bg, #f5ead8)', border: 'none', borderRadius: '999px',
    fontSize: '1rem', fontWeight: 400, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    fontFamily: 'var(--og-fh, Georgia, serif)',
  },
  navBtn: {
    flex:1, padding:'0.65rem 0.5rem',
    background:'var(--og-a100, #fff2eb)', border:'none',
    color:'var(--og-a800, #643312)', borderRadius:'999px', fontSize:'0.82rem',
    fontWeight:600, cursor:'pointer', textAlign:'center', fontFamily:'inherit',
  },
  footHint: { fontSize:'0.68rem', color:'var(--og-n500, #a19786)', textAlign:'center', letterSpacing:'0.03em' },
  wakeBtn: {
    width:'100%', marginTop:'0.5rem', padding:'0.7rem', borderRadius:'999px',
    border:'none', background:'var(--og-s100, #f0fae1)',
    color:'var(--og-s800, #3d472b)', fontSize:'0.85rem', fontWeight:600, cursor:'pointer',
    textAlign:'center', fontFamily:'inherit',
  },
  wakeBtnOn: {
    width:'100%', marginTop:'0.5rem', padding:'0.7rem', borderRadius:'999px',
    border:'none', background:'var(--og-s200, #e1eecc)',
    color:'var(--og-s900, #272e1b)', fontSize:'0.85rem', fontWeight:700, cursor:'pointer',
    textAlign:'center', fontFamily:'inherit',
  },
  awaitRow:  { display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', padding:'0.75rem' },
  awaitDot:  { width:8, height:8, borderRadius:'50%', background:'var(--og-accent, #c67139)', animation:'svP 1.2s infinite', flexShrink:0 },
  awaitText: { fontSize:'0.84rem', color:'var(--og-a700, #8c491a)', fontWeight:600 },

  // Live panel
  livePanel: {
    background:'var(--og-n900, #2e2b25)',
    borderRadius:'var(--og-rl, 28px)', padding:'1.2rem 1.3rem',
    display:'flex', flexDirection:'column', gap:'0.7rem',
    boxShadow:'var(--og-shadow, 0 3px 10px rgba(46,43,37,0.16))',
  },
  liveHeader:  { display:'flex', alignItems:'center', gap:'0.5rem' },
  liveDot: (connected, connecting) => ({
    width:9, height:9, borderRadius:'50%', flexShrink:0,
    background: connected ? '#27ae60' : connecting ? '#e6a020' : '#666',
    animation: connected ? 'svP 2s infinite' : connecting ? 'svP 0.7s infinite' : 'none',
  }),
  liveTitle:   { flex:1, fontSize:'0.7rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(245,234,216,.65)' },
  chatScroll:  { display:'flex', flexDirection:'column', gap:'0.35rem', maxHeight:130, overflowY:'auto' },
  idleMsg:     { color:'rgba(245,234,216,.55)', fontSize:'0.9rem', lineHeight:1.65, textAlign:'center', padding:'0.5rem 0' },
  bubble:      { maxWidth:'86%', padding:'0.55rem 0.9rem', borderRadius:'18px', fontSize:'0.875rem', lineHeight:1.55 },
  userBubble:  { background:'rgba(198,113,57,.35)', color:'#f5ead8', borderBottomRightRadius:'6px', marginLeft:'auto' },
  aiBubble:    { background:'rgba(122,138,94,.35)', color:'#f0fae1', borderBottomLeftRadius:'6px' },
  fftRow:      { display:'flex', alignItems:'center', gap:2, height:24, justifyContent:'center' },
  fftBar:      { width:3, borderRadius:2, background:'rgba(255,198,165,0.8)', transition:'height 0.05s' },
  suggestBtn:  { padding:'0.8rem 1rem', background:'var(--og-accent, #c67139)', color:'var(--og-bg, #f5ead8)', border:'none', borderRadius:'999px', fontSize:'0.9rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' },
  micStartBtn: { padding:'0.95rem 1rem', width:'100%', background:'var(--og-accent, #c67139)', color:'var(--og-bg, #f5ead8)', border:'none', borderRadius:'999px', fontSize:'0.98rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit', boxShadow:'none' },
  errMsg:      { fontSize:'0.75rem', color:'#ffc6a5', textAlign:'center', lineHeight:1.5 },
  suggestHint: { fontSize:'0.66rem', color:'rgba(245,234,216,.45)', textAlign:'center', marginTop:'-0.3rem' },
  liveHint:    { fontSize:'0.63rem', color:'rgba(245,234,216,.35)', textAlign:'center', letterSpacing:'0.03em' },
}

if (typeof document !== 'undefined' && !document.getElementById('sv-companion-anim')) {
  const el = document.createElement('style'); el.id = 'sv-companion-anim'
  el.textContent = '@keyframes svP{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.35;transform:scale(1.5)}}'
  document.head.appendChild(el)
}
